import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Users, FolderKanban, TrendingUp, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/* --------------------------------------------------------------------------
   Marketing Department overview dashboard.
   Aggregates every marketing team's tasks into three views:
     1. Total orders by status (cards)
     2. Status breakdown by team (horizontal stacked bars)
     3. Status breakdown by project (horizontal stacked bars)
---------------------------------------------------------------------------*/

const STATUSES = ["To Do", "Content Generated", "Working", "Editing Pending", "Ready To Publish", "Published"];

const STATUS_META = {
  "To Do":              { dot: "bg-gray-400",    bar: "#9CA3AF", cardAccent: "from-gray-400 to-slate-500",       chip: "bg-gray-100 text-gray-700" },
  "Content Generated":  { dot: "bg-slate-500",   bar: "#64748B", cardAccent: "from-slate-500 to-slate-700",      chip: "bg-slate-100 text-slate-700" },
  "Working":            { dot: "bg-amber-500",   bar: "#F59E0B", cardAccent: "from-amber-500 to-orange-500",     chip: "bg-amber-100 text-amber-800" },
  "Editing Pending":    { dot: "bg-orange-500",  bar: "#F97316", cardAccent: "from-orange-500 to-red-500",       chip: "bg-orange-100 text-orange-800" },
  "Ready To Publish":   { dot: "bg-sky-500",     bar: "#0EA5E9", cardAccent: "from-sky-500 to-indigo-500",       chip: "bg-sky-100 text-sky-800" },
  "Published":          { dot: "bg-emerald-500", bar: "#10B981", cardAccent: "from-emerald-500 to-teal-500",     chip: "bg-emerald-100 text-emerald-800" },
};

const emptyByStatus = () => STATUSES.reduce((m, s) => ((m[s] = 0), m), {});

export default function MarketingDepartment() {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [tasksByTeam, setTasksByTeam] = useState({}); // teamId → tasks[]

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: teamList } = await api.get("/marketing-teams");
        if (!alive) return;
        setTeams(teamList || []);
        const results = await Promise.all(
          (teamList || []).map((t) =>
            api
              .get(`/marketing-teams/${t.id}/tasks`, { params: { include_history: false, limit: 20000 } })
              .then((r) => [t.id, r.data || []])
              .catch(() => [t.id, []])
          )
        );
        if (!alive) return;
        const map = {};
        for (const [id, rows] of results) map[id] = rows;
        setTasksByTeam(map);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const allTasks = useMemo(() => Object.values(tasksByTeam).flat(), [tasksByTeam]);

  const totalByStatus = useMemo(() => {
    const m = emptyByStatus();
    for (const t of allTasks) {
      const s = STATUSES.includes(t.status) ? t.status : "To Do";
      m[s]++;
    }
    return m;
  }, [allTasks]);

  const teamsRows = useMemo(() => {
    return teams
      .map((team) => {
        const rows = tasksByTeam[team.id] || [];
        const byStatus = emptyByStatus();
        for (const t of rows) {
          const s = STATUSES.includes(t.status) ? t.status : "To Do";
          byStatus[s]++;
        }
        return { id: team.id, name: team.name, total: rows.length, byStatus };
      })
      .sort((a, b) => b.total - a.total);
  }, [teams, tasksByTeam]);

  const projectsRows = useMemo(() => {
    const byProj = new Map();
    for (const t of allTasks) {
      const name = (t.project_name || "").trim() || "(no project)";
      if (!byProj.has(name)) byProj.set(name, { name, total: 0, byStatus: emptyByStatus() });
      const row = byProj.get(name);
      const s = STATUSES.includes(t.status) ? t.status : "To Do";
      row.byStatus[s]++;
      row.total++;
    }
    return Array.from(byProj.values()).sort((a, b) => b.total - a.total);
  }, [allTasks]);

  const grandTotal = allTasks.length;

  if (loading) {
    return (
      <div className="p-8 md:p-12 flex items-center justify-center" data-testid="marketing-dept-loading">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
        <span className="ml-3 text-sm text-slate-500">Loading marketing department…</span>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="marketing-dept-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white flex items-center justify-center shadow-lg">
            <Megaphone className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Marketing Team</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {teams.length} {teams.length === 1 ? "team" : "teams"} · {grandTotal} total orders
            </p>
          </div>
        </div>
      </div>

      {/* Status cards */}
      <section data-testid="marketing-dept-status-cards">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Orders by status</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STATUSES.map((s) => {
            const meta = STATUS_META[s];
            const count = totalByStatus[s] || 0;
            const pct = grandTotal ? Math.round((count / grandTotal) * 100) : 0;
            return (
              <div
                key={s}
                className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`status-card-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${meta.cardAccent}`} />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{s}</div>
                    <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{count}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{pct}% of total</div>
                  </div>
                  <span className={`h-2.5 w-2.5 rounded-full ${meta.dot} shrink-0 mt-1.5`} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Status by Team */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="marketing-dept-by-team">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Status by team</h2>
          </div>
          <StatusLegend />
        </div>
        <div className="divide-y divide-slate-100">
          {teamsRows.length === 0 ? (
            <EmptyRow label="No teams yet. Create a team from the sidebar." />
          ) : (
            teamsRows.map((row) => (
              <StackedRow
                key={row.id}
                label={
                  <Link
                    to={`/marketing/teams/${row.id}`}
                    className="font-medium text-slate-800 hover:text-purple-600 inline-flex items-center gap-1 group"
                    data-testid={`team-row-link-${row.id}`}
                  >
                    {row.name}
                    <ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                }
                total={row.total}
                byStatus={row.byStatus}
                testIdPrefix={`team-row-${row.id}`}
              />
            ))
          )}
        </div>
      </section>

      {/* Status by Project */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="marketing-dept-by-project">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FolderKanban className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Status by project</h2>
          </div>
          <StatusLegend />
        </div>
        <div className="divide-y divide-slate-100">
          {projectsRows.length === 0 ? (
            <EmptyRow label="No orders yet. Create one from any marketing team sheet." />
          ) : (
            projectsRows.map((row) => (
              <StackedRow
                key={row.name}
                label={<span className="font-medium text-slate-800">{row.name}</span>}
                total={row.total}
                byStatus={row.byStatus}
                testIdPrefix={`project-row-${row.name.replace(/\s+/g, "-").toLowerCase()}`}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function StatusLegend() {
  return (
    <div className="hidden md:flex items-center gap-3 flex-wrap">
      {STATUSES.map((s) => (
        <div key={s} className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`legend-${s.replace(/\s+/g, "-").toLowerCase()}`}>
          <span className={`h-2 w-2 rounded-full ${STATUS_META[s].dot}`} />
          <span>{s}</span>
        </div>
      ))}
    </div>
  );
}

function StackedRow({ label, total, byStatus, testIdPrefix }) {
  return (
    <div className="px-5 py-3 flex items-center gap-4" data-testid={testIdPrefix}>
      <div className="w-48 shrink-0 min-w-0 flex items-center gap-2">
        <div className="truncate">{label}</div>
      </div>
      <div className="flex-1 min-w-0">
        {total === 0 ? (
          <div className="h-6 rounded bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-400 italic">
            No orders yet
          </div>
        ) : (
          <div className="h-6 flex rounded overflow-hidden shadow-sm" title={`${total} orders`}>
            {STATUSES.map((s) => {
              const count = byStatus[s] || 0;
              if (!count) return null;
              const pct = (count / total) * 100;
              return (
                <div
                  key={s}
                  style={{ width: `${pct}%`, backgroundColor: STATUS_META[s].bar }}
                  title={`${s}: ${count} (${Math.round(pct)}%)`}
                  className="flex items-center justify-center text-[10px] font-semibold text-white tabular-nums"
                  data-testid={`${testIdPrefix}-seg-${s.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {pct >= 10 && <span className="truncate px-1">{count}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="w-20 shrink-0 text-right tabular-nums">
        <div className="text-sm font-semibold text-slate-800">{total}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">orders</div>
      </div>
    </div>
  );
}

function EmptyRow({ label }) {
  return <div className="px-5 py-10 text-center text-sm text-slate-400 italic">{label}</div>;
}
